import { useCallback, useState } from 'react';
import type { HelpAnchor } from '../help/manifest';

/** Hook that owns the help-drawer's open state + current anchor.
 *  Single source of truth: EditorPage instantiates this once and
 *  every `?` button on every panel / dialog hits the returned
 *  `openAt` to open the drawer at the right section.
 *
 *  Why a hook rather than a context: the editor has exactly one
 *  drawer per page and one EditorPage per active tab. A context
 *  would add ceremony without a multi-consumer payoff. */
export interface HelpDrawerState {
  /** True when the drawer is mounted-open. */
  open: boolean;
  /** Section currently visible. `null` means the drawer is closed
   *  OR was opened without a specific section (renders the index). */
  anchor: HelpAnchor | null;
  /** Open the drawer at a specific section. Pass `'index'` for the
   *  top-level overview. */
  openAt: (anchor: HelpAnchor) => void;
  /** Close the drawer. Leaves the last-viewed anchor on state so a
   *  subsequent re-open (without an explicit `openAt`) restores it. */
  close: () => void;
  /** Used by the drawer's own onOpenChange + TOC clicks. Setting
   *  `false` closes; setting `true` reopens at the last anchor. */
  setOpen: (open: boolean) => void;
  /** Navigate to a different section without closing — the TOC and
   *  in-content links use this. */
  navigate: (anchor: HelpAnchor) => void;
}

export function useHelpDrawer(): HelpDrawerState {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HelpAnchor | null>(null);

  const openAt = useCallback((a: HelpAnchor) => {
    setAnchor(a);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const navigate = useCallback((a: HelpAnchor) => {
    setAnchor(a);
  }, []);

  return { open, anchor, openAt, close, setOpen, navigate };
}
